import { useCallback, useRef, useState } from "react";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment, { Moment } from "moment";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import ImageDropField from "../ImageDropField/ImageDropField";
import { useUserState } from "../../../state/user";
import {
  createIdentifyingDocument,
  uploadPicture
} from "../../../services/professionals";
import { IdentifyingDocument } from "../../../types/professional";
import { outlinedFieldSx } from "../../../utils/fieldStyles";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

const labelSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary,
  mb: 0.5
};

interface UploadedSide {
  file: File;
  url: string;
}

/** Spacing between the stacked blocks of the form. */
const fieldBlockSx = { mb: 2 };
const lastFieldBlockSx = { mb: 3 };
const errorSx = { mt: 2 };

const numberSx = {
  ...typography.body.lSemibold,
  color: semanticColors.text.primary
};

/**
 * Registers a renewed document.
 *
 * Renewing files a new document rather than editing the old one — the expiry
 * date is write-once on the server, and a card that has expired is replaced,
 * not amended. The previous one stays on the record as history, and the newest
 * is what the screens read.
 *
 * The number is shown but not editable: a renewed card keeps it, and a genuinely
 * different number is a different document, which is not what this dialog is
 * for.
 */
export default function UpdateDocumentDialog({
  open,
  onClose,
  title,
  document,
  professional,
  type,
  defaultNumber,
  onUpdated
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** The document being replaced. Absent when filing the first one. */
  document?: IdentifyingDocument;
  professional: boolean;
  type: number;
  /** Prefills the number when there is no document to carry it over from. */
  defaultNumber?: string;
  onUpdated: () => void;
}) {
  const { t } = useTranslation();
  const { token, refreshUser } = useUserState();
  const [number, setNumber] = useState(
    () => document?.documentId ?? defaultNumber ?? ""
  );
  const [expiry, setExpiry] = useState<Moment | null>(null);
  const [front, setFront] = useState<File | null>(null);
  const [back, setBack] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * What is already in storage, remembered across attempts.
   *
   * Each entry is tied to the file that produced it, so choosing a different
   * image discards the old address rather than filing a stale one. Without
   * this, a retry after one side failed would upload the side that had already
   * worked a second time, leaving an unreferenced object behind on every go.
   */
  const uploaded = useRef<{ front?: UploadedSide; back?: UploadedSide }>({});

  // Everything is required: a replacement with a side missing would leave the
  // record worse than the card it replaces.
  const canSubmit = Boolean(
    number.trim() && expiry?.isValid() && front && back && !saving
  );

  /** Uploads a side, unless this exact file is already in storage. */
  const keep = useCallback(
    async (side: "front" | "back", file: File): Promise<string> => {
      const known = uploaded.current[side];
      if (known && known.file === file) return known.url;

      const url = await uploadPicture({ authToken: token, file });
      uploaded.current[side] = { file, url };
      return url;
    },
    [token]
  );

  const onSubmit = useCallback(async () => {
    if (!token || !expiry || !front || !back || !number.trim()) return;
    setSaving(true);
    setError(null);
    try {
      // Uploaded first: the document is only registered once both sides are
      // actually stored, so a failure here leaves no half-made record behind.
      //
      // Settled rather than all-or-nothing, so that a side which succeeds while
      // the other fails is still remembered. The attempt fails either way, but
      // the next one only has to send what is genuinely missing.
      const [frontResult, backResult] = await Promise.allSettled([
        keep("front", front),
        keep("back", back)
      ]);

      if (
        frontResult.status === "rejected" ||
        backResult.status === "rejected"
      ) {
        throw new Error("Upload failed");
      }

      const frontUrl = frontResult.value;
      const backUrl = backResult.value;

      await createIdentifyingDocument({
        authToken: token,
        input: {
          professional,
          type,
          documentId: number.trim(),
          front: frontUrl,
          back: backUrl,
          expirationDate: expiry.format("YYYY-MM-DD")
        }
      });

      await refreshUser();
      onUpdated();
    } catch {
      setError(t("identifyingDocuments.submitError"));
    } finally {
      setSaving(false);
    }
  }, [
    token,
    keep,
    expiry,
    front,
    back,
    number,
    professional,
    type,
    refreshUser,
    onUpdated,
    t
  ]);

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={title}
      maxWidth="sm"
    >
      <DialogContent>
        <Box sx={fieldBlockSx}>
          <Typography sx={labelSx}>
            {t("identifyingDocuments.number")}
          </Typography>
          {document ? (
            /* A renewed card keeps its number; a different number would be a
               different document, which is not what this dialog does. */
            <Typography sx={numberSx}>{document.documentId}</Typography>
          ) : (
            <TextField
              value={number}
              onChange={event => setNumber(event.target.value)}
              fullWidth
              sx={outlinedFieldSx}
            />
          )}
        </Box>

        <Box sx={lastFieldBlockSx}>
          <Typography sx={labelSx}>
            {t("identifyingDocuments.expiryDate")}
          </Typography>
          <DatePicker
            value={expiry}
            onChange={setExpiry}
            // A card that has already expired is not a renewal.
            minDate={moment()}
            slotProps={{ textField: { fullWidth: true, sx: outlinedFieldSx } }}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <ImageDropField
              label={t("identifyingDocuments.front")}
              file={front}
              onSelect={setFront}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ImageDropField
              label={t("identifyingDocuments.back")}
              file={back}
              onSelect={setBack}
            />
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={errorSx}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1}>
          <ActionButton
            label={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <ActionButton
            label={t("identifyingDocuments.add")}
            onClick={onSubmit}
            loading={saving}
            disabled={!canSubmit}
          />
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
